import React, {useCallback, useMemo, useState} from 'react';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FlatButton} from '@pb/kit-component-button';
import {ButtonGroup} from '@pb/kit-component-button-group';
import {defaultChartConfig} from '@pb/kit-component-chart-v2';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {FormSection} from '@pb/kit-component-form-section';
import {Icons} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';

import {SERIES, buildChartData} from './chart-data';
import {getDefaultGrouping, getPresetRange, todayUtc} from './chart-period';
import {ChartRangeControl} from './chart-range-control';
import {HistoryChart} from './history-chart';
import {buildProductChartData} from './product-chart-data';
import {useAccountProductHistoryChartData} from './useAccountProductHistoryChartData';
import {useAccountUserHistoryChartData} from './useAccountUserHistoryChartData';

import * as styles from './user-history-chart.module.styl';

import type {ChartRange, HistoryGrouping} from './chart-period';
import type {ScrollStackedChartConfig} from '@pb/kit-component-chart-v2';
import type {LegendItemClickedEvent} from '@pb/kit-component-chart-v2/dist/common-types/generated-event-types/events';

type Props = {accountId: number};
const TABS = [{id: 'product', label: 'By product'}, {id: 'platform', label: 'Platform access types'}];
const GROUPINGS: {value: HistoryGrouping, label: string}[] = [
    {value: 'DAY', label: 'Day'}, {value: 'WEEK', label: 'Week'}, {value: 'MONTH', label: 'Month'},
];

export const AccountUserHistoryChart = ({accountId}: Props) => {
    const today = todayUtc();
    const [tab, setTab] = useState('platform');
    const [range, setRange] = useState<ChartRange>(() => getPresetRange('Current week', today));
    const [groupBy, setGroupBy] = useState<HistoryGrouping>('DAY');
    const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
    const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());
    const platform = useAccountUserHistoryChartData({
        accountId, startDate: range.startDate, endDate: range.endDate, groupBy, enabled: tab === 'platform',
    });
    const product = useAccountProductHistoryChartData({
        accountId, startDate: range.startDate, endDate: range.endDate, groupBy, enabled: tab === 'product',
    });
    const {isLoading, loadingError, loadData} = tab === 'product' ? product : platform;
    const hasData = tab === 'product'
        ? product.rows.some(row => product.products.some(({productKey}) => row.countsByProduct[productKey] > 0))
        : platform.rows.some(row => SERIES.some(({key}) => row[key] > 0));
    const includeYear = range.startDate.slice(0, 4) !== range.endDate.slice(0, 4);
    const dataSourceProps = useMemo(
        () => tab === 'product'
            ? buildProductChartData(product.products, product.rows, hiddenProducts, {includeYear, groupBy})
            : buildChartData(platform.rows, hiddenSeries, {includeYear, groupBy}),
        [tab, product.products, product.rows, platform.rows, hiddenProducts, hiddenSeries, includeYear, groupBy],
    );
    const handleRangeChange = useCallback((next: ChartRange) => {
        setRange(next);
        setGroupBy(getDefaultGrouping(next));
    }, []);


    const handleLegendItemClicked = useCallback<LegendItemClickedEvent>(event => {
        event.preventDefault();
        const series = dataSourceProps.dataset.find(item => item.seriesName === event.data.datasetName);
        if (!series) {
            return;
        }
        const name = series.id;
        const update = tab === 'product' ? setHiddenProducts : setHiddenSeries;
        update(previous => {
            const next = new Set(previous);
            if (next.has(name)) {
                next.delete(name);
            } else {
                next.add(name);
            }
            return next;
        });
    }, [tab, dataSourceProps]);
    const config: ScrollStackedChartConfig = useMemo(() => ({
        ...defaultChartConfig,
        labelDisplay: 'none',
        labelStep: 1,
        scrollToEnd: 1,
        showSum: 0,
        showValues: 0,
        interactiveLegend: 1,
        showLegend: 1,
        drawCrossLine: 1,
        crossLineColor: '#777777',
        crossLineAlpha: 6,
        plotColorInTooltip: 0,
        formatNumberScale: 0,
        decimals: 0,
        yAxisMinValue: 0,
        baseFont: "ui-sans-serif, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        baseFontSize: 10,
        baseFontColor: '#616A75',
        labelFontColor: '#616A75',
        yAxisValueFontColor: '#616A75',
        yAxisValueFontSize: 10,
        outCnvBaseFontSize: 10,
        numDivLines: 3,
        divLineColor: '#DADEE4',
        divLineAlpha: 100,
        divLineDashed: 1,
        divLineDashLen: 3,
        divLineDashGap: 3,
        plotSpacePercent: 10,
        chartTopMargin: 8,
        chartRightMargin: 8,
        chartBottomMargin: 0,
        legendPadding: 8,
        showHoverEffect: 0,
        showTooltip: 0,
        showShadow: 0,
        legendShadow: 0,
        legendBorderAlpha: 0,
        legendItemFontSize: 10,
        legendItemFontColor: '#616A75',
        drawCustomLegendIcon: 1,
        legendIconSides: 4,
        legendIconStartAngle: 45,
    }), []);

    let content;
    if (isLoading) {
        content = <div className={styles.state} aria-label="Loading seat allocation history"/>;
    } else if (loadingError) {
        content = (
            <div className={styles.state}>
                <Message color="error">Unable to load seat allocation history.</Message>
                <FlatButton onClick={loadData}>Retry</FlatButton>
            </div>
        );
    } else if (hasData) {
        content = (
            <HistoryChart
                config={config}
                dataSourceProps={dataSourceProps}
                onLegendItemClicked={handleLegendItemClicked}
                showTotal={tab !== 'product'}
            />
        );
    } else {
        content = (
            <div className={styles.state} role="status">
                No seat allocation history is available for the selected period.
            </div>
        );
    }

    return (
        <FormSection className={styles.section} legend="Seat Allocation Over Time">
            <div className={styles.header}>
                <div>
                    <div className={styles.title}>Seat Allocation Over Time</div>
                    <div className={styles.subtitle}>
                        {tab === 'product' ? 'Seats by product' : 'Platform seats by access type'}
                    </div>
                </div>
                <div className={styles.controls}>
                    <ButtonGroup
                        className={styles.tabs}
                        role="group"
                        aria-label="Seat allocation view"
                        options={TABS}
                        selectedOptionId={tab}
                        onChange={option => {
                            if (option.id !== tab) {
                                setTab(option.id);
                                handleRangeChange(getPresetRange('Current week', today));
                            }
                        }}
                    />
                    <ChartRangeControl
                        value={range}
                        today={today}
                        onChange={handleRangeChange}
                    />
                    <DropdownMenu
                        className={styles.groupingMenu}
                        hideOnItemClick={true}
                        defaultAnchorProps={{className: styles.control, variant: 'secondary',
                            children: (
                                <>
                                    <span className={styles.controlLabel}>Group By: </span>
                                    {GROUPINGS.find(option => option.value === groupBy)!.label}
                                </>
                            )}}>
                        {GROUPINGS.map(option => (
                            <DropdownMenuItem
                                key={option.value}
                                caption={option.label}
                                selected={groupBy === option.value}
                                iconAfter={groupBy === option.value ? Icons.CHECK : undefined}
                                onClick={() => setGroupBy(option.value)}
                            />
                        ))}
                    </DropdownMenu>
                </div>
            </div>
            <div className={styles.content}>
                <BoxLoading loading={isLoading}>{content}</BoxLoading>
            </div>
        </FormSection>
    );
};
